import { toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../lib/shigo-api-client';
import {
  ChangelogLookupStatus,
  changelogDemandPartnerLookupOptionSchema,
  changelogLookupPageSchema,
  changelogPageSchema,
  changelogSchema,
  changelogUserLookupOptionSchema,
  type ChangelogFilter,
} from './model';

interface ChangelogClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

interface ChangelogRequestOptions {
  includePagination?: boolean;
}

export function createChangelogClient({ config, accessToken, fetcher }: ChangelogClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);
  const lookupPageSize = 1000;

  return {
    list: (filter: ChangelogFilter) =>
      api.request({
        path: `/api/change-log?${serializeChangelogFilter(filter)}`,
        schema: changelogPageSchema,
      }),
    exportList: (filter: ChangelogFilter) =>
      api.request({
        path: `/api/change-log/export?${serializeChangelogFilter(filter, { includePagination: false })}`,
        schema: z.array(changelogSchema),
      }),
    listPublishers: () =>
      api.request({
        path: `/api/publishers?${toSearchParams({
          page: 0,
          size: lookupPageSize,
          status: ChangelogLookupStatus.Active,
        })}`,
        schema: changelogLookupPageSchema,
      }),
    listPlacements: () =>
      api.request({
        path: `/api/placements?${toSearchParams({
          page: 0,
          size: lookupPageSize,
          status: ChangelogLookupStatus.Active,
        })}`,
        schema: changelogLookupPageSchema,
      }),
    listIntegrations: () =>
      api.request({
        path: `/api/dps/integrations?${toSearchParams({
          page: 0,
          size: lookupPageSize,
          status: ChangelogLookupStatus.Active,
        })}`,
        schema: changelogLookupPageSchema,
      }),
    listDemandPartners: () =>
      api.request({
        path: '/api/dps/all',
        schema: z.array(changelogDemandPartnerLookupOptionSchema),
      }),
    listUsers: () =>
      api.request({
        path: `/api/users/list-all?${toSearchParams({
          page: 0,
          size: lookupPageSize,
          enabled: true,
        })}`,
        schema: z.array(changelogUserLookupOptionSchema),
      }),
    listLineItems: () =>
      api.request({
        path: `/api/campaign/line-items?${toSearchParams({
          page: 0,
          size: lookupPageSize,
        })}`,
        schema: changelogLookupPageSchema,
      }),
  };
}

export function serializeChangelogFilter(
  filter: ChangelogFilter,
  { includePagination = true }: ChangelogRequestOptions = {},
) {
  return toSearchParams({
    ...(includePagination ? { page: filter.page, size: filter.size } : {}),
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    publisherIds: filter.publisherIds.length ? filter.publisherIds : undefined,
    placementIds: filter.placementIds.length ? filter.placementIds : undefined,
    integrationIds: filter.integrationIds.length ? filter.integrationIds : undefined,
    demandPartnerIds: filter.demandPartnerIds.length ? filter.demandPartnerIds : undefined,
    createdBy: filter.createdBy || undefined,
    lineItemIds: filter.lineItemIds.length ? filter.lineItemIds : undefined,
    dateFrom: filter.dateFrom,
    dateTo: filter.dateTo,
  }).toString();
}
